// eslint-disable-next-line no-restricted-imports
import type { ModelType } from '@sb/robot';
import { convertDegreesToRadians, isApproximatelyEqual } from '@sb/utilities';

import type { ArmJointPositions } from './ArmJointPositions';

export const RO2_JOINT_ANGLES_FOAM_CASE: ArmJointPositions = [
  -0, -105, 163, -140, 180, 186,
].map((degrees) => convertDegreesToRadians(degrees)) as ArmJointPositions;

export const RO2_UNBOX_TARGET_JOINT_ANGLES = [0, -50, 120, 20, -90, 0].map(
  convertDegreesToRadians,
) as ArmJointPositions;

export const RO2_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES = [
  0, -105, 160, -120, 180, 186,
].map(convertDegreesToRadians) as ArmJointPositions;

/**
 * RO2's intermediate pose for when MoveIt2 plans against the primitive (sphere
 * or capsule) collision model rather than the meshes.
 *
 * The spheres envelope the real geometry by ~29 mm on average, so at the
 * mesh-model intermediate above (J3 = -120 deg) forearm↔wrist_3 overlaps by
 * 7.0 mm and the planner refuses the pose. J3 = -105 deg clears that pair
 * (~1.2 mm per degree of J3), and J2 = 158 deg takes the two
 * elbow-onto-shoulder pairs from 0.7 / 1.8 mm to real margin. Closest pair here
 * is forearm↔wrist_3 at +11.0 mm, so this pose needs no ignored pairs — keep it
 * that way if you move these angles.
 */
export const RO2_PRIMITIVE_COLLISION_MODEL_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES =
  [0, -105, 158, -105, 180, 186].map(
    convertDegreesToRadians,
  ) as ArmJointPositions;

export const THOR_JOINT_ANGLES_BOX: ArmJointPositions = [
  0, 0, 180, 180, 0, 0,
].map((degrees) => convertDegreesToRadians(degrees)) as ArmJointPositions;

export const THOR_UNBOX_TARGET_JOINT_ANGLES = [0, -42, 120, -12, 90, 0].map(
  convertDegreesToRadians,
) as ArmJointPositions;

export const THOR_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES = [
  0, 0, 160, 180, 0, 0,
].map(convertDegreesToRadians) as ArmJointPositions;

export const SPARK_JOINT_ANGLES_BOX: ArmJointPositions = THOR_JOINT_ANGLES_BOX;
export const SPARK_UNBOX_TARGET_JOINT_ANGLES: ArmJointPositions =
  THOR_UNBOX_TARGET_JOINT_ANGLES;
export const SPARK_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES: ArmJointPositions =
  THOR_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES;

// Model-specific boxing/unboxing angles
const BOXING_JOINT_ANGLES_BY_MODEL: Record<ModelType, ArmJointPositions> = {
  RO1: RO2_JOINT_ANGLES_FOAM_CASE,
  Thor: THOR_JOINT_ANGLES_BOX,
  Spark: SPARK_JOINT_ANGLES_BOX,
};

const UNBOX_TARGET_JOINT_ANGLES_BY_MODEL: Record<ModelType, ArmJointPositions> =
  {
    RO1: RO2_UNBOX_TARGET_JOINT_ANGLES,
    Thor: THOR_UNBOX_TARGET_JOINT_ANGLES,
    Spark: SPARK_UNBOX_TARGET_JOINT_ANGLES,
  };

const INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL: Record<
  ModelType,
  ArmJointPositions
> = {
  RO1: RO2_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
  Thor: THOR_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
  Spark: SPARK_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
};

// Only RO2's intermediate pose differs between the two collision models. Thor
// and Spark hold J3 at 180 deg at both the intermediate and the boxing pose, so
// theirs is clear under either.
const PRIMITIVE_COLLISION_MODEL_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL: Record<
  ModelType,
  ArmJointPositions
> = {
  RO1: RO2_PRIMITIVE_COLLISION_MODEL_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
  Thor: THOR_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
  Spark: SPARK_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
};

export function getBoxingJointAngles(modelType: ModelType): ArmJointPositions {
  return BOXING_JOINT_ANGLES_BY_MODEL[modelType];
}

/**
 * The pose the arm ends up in after unboxing. Clears by at least 67 mm on every
 * variant under either collision model, so it is the same for both.
 */
export function getUnboxTargetAngles(modelType: ModelType): ArmJointPositions {
  return UNBOX_TARGET_JOINT_ANGLES_BY_MODEL[modelType];
}

/**
 * Boxing/unboxing poses for a model.
 *
 * `primitiveCollisionModel` is whether MoveIt2 plans against the primitive
 * collision model, which it does while the jog barrier enforces it
 * (`isPrimitiveCollisionModelLoadedForModel` in `@sb/robot`). It is passed in because
 * this module is shared between the frontend and botman, which read flags
 * through different mechanisms. It selects the intermediate pose — under the
 * primitive model RO2 uses one backed further out — while the unbox target is
 * the same either way. Passing the wrong value targets a pose the planner
 * refuses.
 */
export function getUnboxingAngles(
  modelType: ModelType,
  { primitiveCollisionModel }: { primitiveCollisionModel: boolean },
): {
  unbox: ArmJointPositions;
  intermediate: ArmJointPositions;
} {
  const intermediateByModel = primitiveCollisionModel
    ? PRIMITIVE_COLLISION_MODEL_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL
    : INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL;

  return {
    unbox: UNBOX_TARGET_JOINT_ANGLES_BY_MODEL[modelType],
    intermediate: intermediateByModel[modelType],
  };
}

/**
 * Collision link pairs to ignore during boxing/unboxing moves.
 * These pairs are allowed to collide without triggering a failure.
 */
type CollisionLinkPair = [string, string];

// Common collision pairs shared by all models (end effector and wrist area)
const COMMON_BOXING_IGNORED_COLLISION_PAIRS: CollisionLinkPair[] = [
  // End effector link, even when no EE there is a default 3FG15 collision box
  ['eef_collision', '*'],
  ['wrist_3_cable_link', '*'],
  // Dual Quick Changer link
  ['connector_collision', '*'],
  ['gripper1_collision', '*'],
  ['gripper2_collision', '*'],
];

// Arm-link pairs the collision model reports in contact at the boxing pose that
// the real arm clears, because the model carries more margin than the pose has
// room for. Only pairs the SRDF leaves enabled can appear here; adjacent links
// are already disabled there.
//
// Thor and Spark need forearm↔shoulder under either collision model. RO2 clears
// all three of its pairs against the meshes, by 6.2 to 16.3 mm against 5-7 mm of
// per-link padding, so it needs them only under the sphere model.
const THOR_BOXING_IGNORED_COLLISION_PAIRS: CollisionLinkPair[] = [
  ...COMMON_BOXING_IGNORED_COLLISION_PAIRS,
  ['forearm_link', 'shoulder_link'],
];

// The pairs RO2 needs under the sphere collision model, which overshoots its
// real geometry by 28.7 mm on average against 6-16 mm of true clearance at the
// boxing pose, so all three report contact:
//
//   forearm↔shoulder    +11.0 mm mesh / -26.9 mm sphere
//   forearm↔wrist_3      +6.2 mm mesh / -30.2 mm sphere
//   upper_arm↔wrist_1   +16.3 mm mesh / -22.3 mm sphere
//
// The boxing pose is fixed by the foam case, so it cannot be backed off the way
// the intermediate was; these are allowed for the duration of the move instead.
const RO2_PRIMITIVE_COLLISION_MODEL_BOXING_IGNORED_COLLISION_PAIRS: CollisionLinkPair[] =
  [
    ...COMMON_BOXING_IGNORED_COLLISION_PAIRS,
    ['forearm_link', 'shoulder_link'],
    ['forearm_link', 'wrist_3_link'],
    ['upper_arm_link', 'wrist_1_link'],
  ];

const BOXING_IGNORED_COLLISION_PAIRS_BY_MODEL: Record<
  ModelType,
  CollisionLinkPair[]
> = {
  RO1: COMMON_BOXING_IGNORED_COLLISION_PAIRS,
  Thor: THOR_BOXING_IGNORED_COLLISION_PAIRS,
  Spark: THOR_BOXING_IGNORED_COLLISION_PAIRS,
};

// Only RO2's list grows. Thor and Spark need forearm↔shoulder either way.
const PRIMITIVE_COLLISION_MODEL_BOXING_IGNORED_COLLISION_PAIRS_BY_MODEL: Record<
  ModelType,
  CollisionLinkPair[]
> = {
  RO1: RO2_PRIMITIVE_COLLISION_MODEL_BOXING_IGNORED_COLLISION_PAIRS,
  Thor: THOR_BOXING_IGNORED_COLLISION_PAIRS,
  Spark: THOR_BOXING_IGNORED_COLLISION_PAIRS,
};

/**
 * Collision link pairs to ignore during boxing/unboxing for a given model type.
 *
 * Under `primitiveCollisionModel` MoveIt2 plans against the primitive collision
 * model, which needs three more pairs allowed for RO2 — see the measured
 * distances above.
 */
export function getBoxingIgnoredCollisionPairs(
  modelType: ModelType,
  { primitiveCollisionModel }: { primitiveCollisionModel: boolean },
): CollisionLinkPair[] {
  const pairsByModel = primitiveCollisionModel
    ? PRIMITIVE_COLLISION_MODEL_BOXING_IGNORED_COLLISION_PAIRS_BY_MODEL
    : BOXING_IGNORED_COLLISION_PAIRS_BY_MODEL;

  return pairsByModel[modelType];
}

// ---------------------------------------------------------------------------
// Per-leg ignore lists for staged boxing approach
// ---------------------------------------------------------------------------

// The reduced list the approach leg (any pose → intermediate) uses: the common
// EE wildcards plus forearm↔shoulder, the single arm-link pair Thor and Spark
// have needed since boxing was introduced. RO2's two extra pairs
// (forearm↔wrist_3, upper_arm↔wrist_1) are withheld so the long variable
// approach stays collision-checked against them.
const BOXING_APPROACH_IGNORED_COLLISION_PAIRS: CollisionLinkPair[] = [
  ...COMMON_BOXING_IGNORED_COLLISION_PAIRS,
  ['forearm_link', 'shoulder_link'],
];

/**
 * Collision pairs to ignore on the approach leg (any pose → intermediate).
 *
 * Under the sphere collision model every model gets the same reduced list, so
 * RO2's two pairs specific to the boxing pose stay collision-checked across the
 * long variable approach and are allowed only on the leg into the boxing pose.
 *
 * Under mesh collision there is nothing to stage — every pair clears naturally
 * — so this gives back the model's ordinary boxing list: the EE wildcards alone
 * for RO2, those plus forearm↔shoulder for Thor and Spark.
 */
export function getBoxingApproachIgnoredPairs(
  modelType: ModelType,
  { primitiveCollisionModel }: { primitiveCollisionModel: boolean },
): CollisionLinkPair[] {
  if (!primitiveCollisionModel) {
    return BOXING_IGNORED_COLLISION_PAIRS_BY_MODEL[modelType];
  }

  return BOXING_APPROACH_IGNORED_COLLISION_PAIRS;
}

/**
 * Per-joint angular proximity thresholds (degrees) defining "close to boxing position".
 * When all joints are within their respective threshold of the boxing angles,
 * FK-based collision checks are suppressed to avoid false positives from URDF
 * mesh overlap at packed joint configurations.
 *
 * Each element corresponds to joints J1–J6.
 */
type JointAnglesDegrees = [number, number, number, number, number, number];

/** Convert a per-model record of J1–J6 angles from degrees to radians. */
function toRadiansByModel(
  degreesByModel: Record<ModelType, JointAnglesDegrees>,
): Record<ModelType, ArmJointPositions> {
  return Object.fromEntries(
    Object.entries(degreesByModel).map(([modelType, degrees]) => [
      modelType,
      degrees.map(convertDegreesToRadians) as ArmJointPositions,
    ]),
  ) as Record<ModelType, ArmJointPositions>;
}

const RO1_BOXING_PROXIMITY_DEGREES: JointAnglesDegrees = [2, 2, 3, 3, 2, 2];

const THOR_BOXING_PROXIMITY_DEGREES: JointAnglesDegrees = [2, 2, 3, 3, 2, 2];

const SPARK_BOXING_PROXIMITY_DEGREES: JointAnglesDegrees = [2, 2, 3, 3, 2, 2];

const BOXING_PROXIMITY_THRESHOLDS_BY_MODEL: Record<
  ModelType,
  ArmJointPositions
> = toRadiansByModel({
  RO1: RO1_BOXING_PROXIMITY_DEGREES,
  Thor: THOR_BOXING_PROXIMITY_DEGREES,
  Spark: SPARK_BOXING_PROXIMITY_DEGREES,
});

export function getBoxingProximityThresholds(
  modelType: ModelType,
): ArmJointPositions {
  return BOXING_PROXIMITY_THRESHOLDS_BY_MODEL[modelType];
}

/**
 * Returns true when all joint angles are within the per-joint proximity thresholds
 * of the boxing position for the given model type.
 */
export function isNearBoxingPosition(
  jointAngles: ArmJointPositions,
  modelType: ModelType,
): boolean {
  const boxingAngles = getBoxingJointAngles(modelType);
  const thresholds = getBoxingProximityThresholds(modelType);

  return jointAngles.every((angle, index) =>
    isApproximatelyEqual(angle, boxingAngles[index], thresholds[index]),
  );
}

// ---------------------------------------------------------------------------
// Boxing intermediate (the pose the staged approach stops at)
// ---------------------------------------------------------------------------

/**
 * The intermediate pose boxing stages through on its way to the boxing pose.
 *
 * Same physical pose as the unboxing intermediate — the geometry is identical
 * in both directions — but a separate accessor so the two can diverge later
 * and the call site reads as "boxing intermediate" not "unboxing intermediate."
 */
export function getBoxingIntermediateAngles(
  modelType: ModelType,
  { primitiveCollisionModel }: { primitiveCollisionModel: boolean },
): ArmJointPositions {
  const byModel = primitiveCollisionModel
    ? PRIMITIVE_COLLISION_MODEL_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL
    : INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL;

  return byModel[modelType];
}

// ---------------------------------------------------------------------------
// Proximity predicate: is the arm at or past the boxing intermediate?
// ---------------------------------------------------------------------------

// Per-joint threshold (degrees) for "near the boxing intermediate." The
// movers need a tight band to confirm arrival; the non-movers need one to
// keep the predicate describing the corridor rather than a region beside it.
// A pose that clears the movers but sits degrees off on the non-movers is not
// on the path between the two configurations, and the allowance the fold
// carries — two extra ignored pairs and no padding on forearm_link /
// wrist_3_link / eef_collision — is only justified along that path.
//
// RO1 collision-avoidance:
//   intermediate [0, -105, 158, -105, 180, 186]°
//   boxing       [0, -105, 163, -140, 180, 186]°
//   Δ:                      +5   -35
//   → J2 (idx 2) and J3 (idx 3) are the only movers. 1° inside the
//     intermediate is enough to confirm we've arrived.
//
// Thor/Spark:
//   intermediate [0, 0, 160, 180, 0, 0]°
//   boxing       [0, 0, 180, 180, 0, 0]°
//   Δ:                  +20
//   → J2 (idx 2) is the only mover.
//
// The fold plans as TYPE_NON_LINEAR, whose MultiPlanner tries Pilz PTP before
// falling back to OMPL. PTP interpolates in joint space, so a non-mover with
// equal endpoints holds its angle for the whole fold and 2° is ample. An OMPL
// fallback can swing them further, but such a path is not the sampled
// corridor either, so widening the band would only admit poses nothing
// validates; those fall through to the two-leg approach instead.
const BOXING_INTERMEDIATE_PROXIMITY_DEGREES_BY_MODEL: Record<
  ModelType,
  JointAnglesDegrees
> = {
  // 2° band on non-moving joints, matching BOXING_PROXIMITY_DEGREES; 1° on movers
  RO1: [2, 2, 1, 1, 2, 2],
  Thor: [2, 2, 1, 2, 2, 2],
  Spark: [2, 2, 1, 2, 2, 2],
};

const BOXING_INTERMEDIATE_PROXIMITY_BY_MODEL: Record<
  ModelType,
  ArmJointPositions
> = toRadiansByModel(BOXING_INTERMEDIATE_PROXIMITY_DEGREES_BY_MODEL);

/**
 * Returns true when the arm is inside the corridor between the boxing
 * intermediate and the boxing pose. Used as a re-press guard: from in here the
 * fold is the only direction that can be planned, because the approach leg
 * withholds the very pairs a corridor pose is likely to be touching, so its
 * start state would be in collision. Skip the approach and go straight to the
 * fold.
 *
 * Per joint: is the angle between the intermediate and the boxing pose, within
 * threshold at either end? That covers both "arrived at the intermediate" and
 * "released partway through the fold". A non-mover has the same angle at both
 * ends, so its band is just the threshold either side.
 */
export function isNearBoxingIntermediate(
  jointAngles: ArmJointPositions,
  modelType: ModelType,
  { primitiveCollisionModel }: { primitiveCollisionModel: boolean },
): boolean {
  const intermediate = getBoxingIntermediateAngles(modelType, {
    primitiveCollisionModel,
  });

  const boxing = getBoxingJointAngles(modelType);
  const thresholds = BOXING_INTERMEDIATE_PROXIMITY_BY_MODEL[modelType];

  return jointAngles.every((angle, index) => {
    const lo = Math.min(intermediate[index], boxing[index]);
    const hi = Math.max(intermediate[index], boxing[index]);

    return angle >= lo - thresholds[index] && angle <= hi + thresholds[index];
  });
}
